<template>
  <div class="login-container w-full h-full flex flex-col pt-2 gap-y-2">
    <div class="w-full flex items-center gap-x-3">
      <div class="border-b-[1px] border-primary flex-1 h-1"></div>
      <span class="flex-1 text-sm text-center text-primary pt-0.5">
        {{ $t('informationNotAvailable') }}
      </span>
      <div class="border-b-[1px] border-primary flex-1 h-1"></div>
    </div>

    <div class="flex flex-col gap-y-2">
      <a
        v-if="i18n.locale.value === 'en'"
        :href="uaePassAuthLink"
        class="flex justify-center px-12"
      >
        <img
          src="@/assets/pngs/UAEPASS_Sign_in_Btn_Default_Black_Active.png"
          alt="UAE Pass login button (dark)"
          class="max-w-72 hidden dark:block"
        />
        <img
          src="@/assets/pngs/UAEPASS_Login_Btn_Outline_Active@2x.png"
          alt="UAE Pass login button (light)"
          class="max-w-72 dark:hidden"
        />
      </a>
      <a v-else :href="uaePassAuthLink" class="flex justify-center px-12">
        <img
          src="@/assets/pngs/AR_UAEPASS_Sign_up_Btn_Outline_Active.png"
          alt="UAE Pass login button (dark)"
          class="max-w-72 hidden dark:block grayscale invert"
        />
        <img
          src="@/assets/pngs/AR_UAEPASS_Sign_up_Btn_Outline_Active.png"
          alt="UAE Pass login button (light)"
          class="max-w-72 dark:hidden"
        />
      </a>

      <small class="w-full text-center px-12">{{ $t('smartPassLoginMessage') }}</small>
    </div>
    <div class="w-full flex items-center gap-x-3">
      <div class="border-b-[1px] border-primary flex-1 h-1"></div>
      <span class="flex-1 text-sm text-center pt-0.5 text-primary">
        {{ $t('loginByEmail') }}
      </span>
      <div class="border-b-[1px] border-primary flex-1 h-1"></div>
    </div>

    <Form
      class="flex flex-col gap-3"
      v-slot="{ resetForm, setFieldValue, validate }"
      @submit="(values) => onSubmit(values, validate, resetForm)"
    >
      <div class="flex gap-1 flex-col">
        <Field
          :rules="requiredRule && validEmail"
          validate-on-input
          validate-on-change
          validate-on-blur
          name="email"
          v-slot="{ field, errors: errs }"
        >
          <TextInput
            :field="field"
            :errors="errs"
            :popover-content="$t('EmailMaxValid')"
            :placeholder="$t('email')"
            :max-length="60"
            input-class="!ps-8 !pe-3 rounded-md py-1"
            wrapper-class="!rounded-md"
            label-class="w-full"
            class="w-full"
            direction="flex-col"
          >
            <template #prefix>
              <i class="ica-custom-fonts-email text-primary-color text-lg text-primary-700" />
            </template>
          </TextInput>
        </Field>
        <Field
          :rules="requiredRule"
          validate-on-input
          validate-on-change
          validate-on-blur
          name="password"
          v-slot="{ field, errors: errs }"
        >
          <TextInput
            :field="field"
            :errors="errs"
            :popover-content="$t('loginPasswordValidation')"
            :placeholder="$t('password')"
            :max-length="20"
            :input-type="passwordInputType"
            input-class="!ps-8 !pe-12 rounded-md py-1"
            wrapper-class="!rounded-md"
            label-class="w-full"
            class="w-full"
            direction="flex-col"
          >
            <template #prefix>
              <i class="ica-custom-fonts-password text-primary-color text-xl text-primary-700" />
            </template>
            <template #suffix>
              <button
                :key="passwordInputType"
                class="flex justify-center items-center"
                @click.prevent.stop="toggleInputType"
              >
                <i
                  :class="passwordInputType == 'text' ? 'icon-eye-off' : 'icon-eye1'"
                  class="text-primary"
                ></i>
              </button>
            </template>
          </TextInput>
        </Field>
        <!-- <GRecaptcha
          ref="gRecaptchaRef"
          class="flex justify-center"
          @successCallback="(args) => setFieldValue('recaptchaResponse', args)"
        ></GRecaptcha> -->
      </div>
      <button
        class="py-1.5 text-center rounded-md bg-primary/80 hover:bg-primary hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
      >
        {{ $t('login') }}
      </button>
    </Form>
    <div class="w-full flex justify-center py-3">
      <RouterLink
        to="/forgot-password"
        class="underline ltr:font-sans text-sm text-primary text-center"
      >
        {{ $t('forgotPassword') }}
      </RouterLink>
    </div>
    <div class="border-b border-brand-gray-300/80"></div>
    <div v-if="globalStore.envConfig" class="w-full text-center text-xs text-primary/80">
      {{ i18n.t('versionNumber', { v: globalStore.envConfig.version }) }}
    </div>
  </div>
</template>

<script setup>
import GRecaptcha from '@/components/GRecaptcha/index.vue'
import TextInput from '@/components/SimpleForm/TextInput.vue'
import { isValidEmail } from '@/composables/useRuleHandler.js'
import { useAuthStore } from '@/stores/auth'
import { useGlobalStore } from '@/stores/global'
import { notify } from 'notiwind'
import { Field, Form } from 'vee-validate'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const globalStore = useGlobalStore()
const router = useRouter()
const authStore = useAuthStore()
const i18n = useI18n({
  messages: {
    en: {
      versionNumber: 'Version: {v}',
      INVALID_EMAIL: 'Must be a valid email address.'
    },
    ar: {
      versionNumber: 'الإصدار: {v}',
      INVALID_EMAIL: 'يجب أن يكون بريدًا إلكترونيًا صالحًا.'
    }
  }
})

function requiredRule(value) {
  if (!`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return true
}

function validEmail(value) {
  return (!!value && isValidEmail(value)) || i18n.t('INVALID_EMAIL')
  // return !!value && isValidEmail(value) ? true
}

const passwordInputType = ref('password')

const uaePassAuthLink = ref('')

const generateUAEPassAuthLink = () => {
  /**
   * in production, replace https://stg-id.uaepass.ae/idshub
   * with ${import.meta.env.VITE_UAEPASS_ENDPOINTS}
   */
  return `https://stg-id.uaepass.ae/idshub/authorize?response_type=code&client_id=sandbox_stage&scope=urn:uae:digitalid:profile:general&state=HnlHOJTkTb66Y5H&redirect_uri=${location.origin}/auth/pass&acr_values=urn:safelayer:tws:policies:authentication:level:low
`
}
uaePassAuthLink.value = generateUAEPassAuthLink()

onMounted(() => {
  // console.info('mounted')
  // const recaptchaUrl = `https://www.google.com/recaptcha/api.js`
  // const recaptchaScript = document.createElement('script')
  // recaptchaScript.setAttribute('src', recaptchaUrl)
  // document.head.appendChild(recaptchaScript)
  // // checkInterval()
  // setInterval(function () {
  //   console.info('1')
  //   if (grecaptcha) {
  //     recaptchaScriptIsReady.value = true
  //     clearInterval(checkInterval)
  //     grecaptcha.ready(function () {
  //       grecaptcha.render('g-recaptcha', {
  //         sitekey: import.meta.env.VITE_RECAPTCHA_KEY
  //       })
  //     })
  //   }
  //   return recaptchaScriptIsReady
  // }, 500)
  // clearInterval(checkInterval)
})

const toggleInputType = () => {
  if (passwordInputType.value == 'password') {
    passwordInputType.value = 'text'
    return
  }

  passwordInputType.value = 'password'
}

const onSubmit = async (form) => {
  // if (!form.recaptchaResponse) {
  //   notify(
  //     {
  //       group: 'error',
  //       title: i18n.t('captchaNotProcessed')
  //     },
  //     1000 * 8
  //   )
  //   return
  // }

  await authStore.emailLogin(form)
  navigateAuth()
}

function navigateAuth() {
  let nextRouteName = 'IndDashboard'

  switch (authStore?.user?.applicationId) {
    case 1: // gcc
      // await getServices(1)
      break
    case 2:
      nextRouteName = 'TCDashboard'
      break
    case 3: // establishment
      nextRouteName = 'EstDashboard'
      break
    case 6: // Citizen
    case 7: // GccCitizen
    case 21: // familyMemberResidencyHolder
      nextRouteName = 'IndDashboard'
      break

    default:
      break
  }

  router.replace({ name: nextRouteName })
}
</script>

<style lang="scss" scoped></style>
